// Plan 20-06 Task 3 — LevelUpModal (META-06 user-visible surface).
//
// Shown at match-end if the player leveled up. Lists the unlocked cosmetic
// names from the inventory-diff (preMatchInventory vs postMatchInventory),
// resolved against COSMETICS_BY_ID for safe lookup. Renders nothing when
// `open === false` to avoid focus-trap activation outside the actual
// celebratory moment.
//
// a11y mirrors FriendInviteModal: role="dialog" + aria-modal="true" +
// useFocusTrap (Phase 17/18 hook — handles Escape, initial focus, restore
// on close). Focus lands on the dismiss Btn by default.
//
// File-ownership: this is a NEW file. WinOverlay.tsx renders it at the END
// of its JSX so Plan 20-07's SHARE-button addition lands in a different
// JSX region (the CTA cluster between rewards grid and the existing
// HOME/REPLAY pair) — minimizing merge conflicts when Plan 20-07 merges.

import type { JSX } from 'react'
import { useIntl } from 'react-intl'

import { Btn } from '../components/Btn'
import { Dialog } from '../components/Dialog'
import { COSMETICS_BY_ID, type CosmeticMeta } from '../data/cosmetic-meta'

export interface LevelUpModalProps {
  readonly open: boolean
  readonly onClose: () => void
  /** New level reached at this match-end. */
  readonly newLevel: number
  /**
   * Cosmetic ids unlocked by crossing this level (derived from the
   * inventory diff between preMatch and postMatch profile fetches).
   * May be empty for levels with no LEVEL_REWARD_SCHEDULE entry.
   */
  readonly unlockedCosmeticIds: ReadonlyArray<string>
}

export function LevelUpModal({
  open,
  onClose,
  newLevel,
  unlockedCosmeticIds,
}: LevelUpModalProps): JSX.Element | null {
  const intl = useIntl()

  if (!open) return null

  // Project ids → meta; drop unknown ids defensively.
  const unlockedMetas: ReadonlyArray<CosmeticMeta> = unlockedCosmeticIds
    .map((id) => COSMETICS_BY_ID.get(id))
    .filter((m): m is CosmeticMeta => m !== undefined)

  return (
    // Center-dialog flavor (UI-SPEC §5): scrim + popIn + focus trap + dismissal
    // live in <Dialog>/ModalScrim. padding:0 so the gold header band bleeds to
    // the rounded panel edge (overflow:hidden clips it).
    <Dialog
      onClose={onClose}
      labelledBy="level-up-modal-title"
      testId="level-up-modal"
      panelStyle={{ padding: 0, overflow: 'hidden' }}
    >
      <div
        style={{
          padding: '16px',
          backgroundColor: 'var(--color-cta-primary)',
          color: 'var(--color-inkwell)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '4px',
        }}
      >
        <h2
          id="level-up-modal-title"
          style={{
            margin: 0,
            fontFamily: 'var(--font-family-display)',
            fontWeight: 700,
            fontSize: '22px',
            textAlign: 'center',
          }}
        >
          {intl.formatMessage({ id: 'level_up_modal.title' })}
        </h2>
        <p
          data-testid="level-up-modal-subtitle"
          style={{
            margin: 0,
            fontFamily: 'var(--font-family-body)',
            fontWeight: 500,
            fontSize: '14px',
            textAlign: 'center',
          }}
        >
          {intl.formatMessage(
            { id: 'level_up_modal.subtitle' },
            { level: newLevel },
          )}
        </p>
      </div>
      <div style={{ padding: '20px' }}>
        {unlockedMetas.length > 0 ? (
          <div
            data-testid="level-up-modal-unlocked-list-wrapper"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              marginBottom: '16px',
            }}
          >
            <h3
              style={{
                margin: 0,
                fontFamily: 'var(--font-family-display)',
                fontWeight: 700,
                fontSize: '14px',
                color: 'var(--color-text-primary)',
              }}
            >
              {intl.formatMessage({ id: 'level_up_modal.unlocked_heading' })}
            </h3>
            <ul
              data-testid="level-up-modal-unlocked-list"
              style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
              }}
            >
              {unlockedMetas.map((meta) => (
                <li
                  key={meta.id}
                  data-testid={`level-up-cosmetic-${meta.id}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontFamily: 'var(--font-family-body)',
                    fontSize: '13px',
                    color: 'var(--color-text-primary)',
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: '14px',
                      height: '14px',
                      borderRadius: '50%',
                      background: meta.preview_color,
                      flexShrink: 0,
                    }}
                  />
                  {intl.formatMessage(
                    { id: 'level_up_modal.unlocked_cosmetic' },
                    {
                      cosmetic_name: intl.formatMessage({
                        id: meta.i18n_key_name,
                      }),
                    },
                  )}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Btn kind="gold" size="md" onClick={onClose}>
            {intl.formatMessage({ id: 'level_up_modal.cta_dismiss' })}
          </Btn>
        </div>
      </div>
    </Dialog>
  )
}
